import { inject, Injectable } from '@angular/core';
import { DateTime } from 'luxon';

import { DemoDataService } from 'src/shared/services/demo-data.service';
import { RemoteAccessAddress, RemoteAccessDevice, SeamAccessCode, SeamService } from 'src/shared/services/seam.service';

import { TranslationPipe } from 'src/shared/pipes/translation.pipe';

// A Seam smart-lock PIN provisioned for a reservation, ready to render.
export interface ReservationLockCode {
  deviceId: number;
  deviceName: string;
  code: string;
  validity: string | null;
}

interface CacheEntry<T> {
  fetchedAt: number;
  request: Promise<T>;
}

// Short enough that a code provisioned moments after a hover still shows up
// soon, long enough that sweeping the cursor across a week of reservation
// cards doesn't refetch the lock list for every popover.
const CACHE_TTL_MS = 60 * 1000;

/**
 * Resolves a stay's Seam smart-lock codes for the schedule hover popover and
 * the reservation detail page.
 */
@Injectable({ providedIn: 'root' })
export class ReservationLockCodesService {
  private readonly seam = inject(SeamService);
  private readonly demoData = inject(DemoDataService);
  private readonly remoteAccessAddresses = new Map<string, CacheEntry<RemoteAccessAddress[]>>();
  private readonly lockCodes = new Map<string, CacheEntry<ReservationLockCode[]>>();

  // The devices endpoint does not return address assignments — the join lives
  // on remote-access-addresses, keyed by each RAA's nested addresses[]. The
  // demo account is skipped: the code lookup is a POST, which DemoInterceptor
  // would answer with the "create an account" prompt.
  async getAssignedLocks(addressId: number): Promise<RemoteAccessDevice[]> {
    if (!addressId || this.demoData.shouldServeTemplates()) {
      return [];
    }
    const remoteAccessAddresses = await this.cached(
      this.remoteAccessAddresses,
      'all',
      () => this.seam.getRemoteAccessAddresses()
    );
    return remoteAccessAddresses
      .filter((raa) => (raa.addresses ?? []).some((address) => Number(address.id) === addressId))
      .flatMap((raa) => raa.remote_access_devices ?? [])
      .filter((device) => device.in_use);
  }

  // With a guest_reservation_id the generate-access-code endpoint only looks up
  // the code already provisioned for the stay (nothing is created), and 404s
  // for devices without one — those are simply skipped.
  getLockCodes(reservationId: number, addressId: number): Promise<ReservationLockCode[]> {
    return this.cached(this.lockCodes, `${reservationId}:${addressId}`, async () => {
      const devices = await this.getAssignedLocks(addressId);
      const codes = await Promise.all(
        devices.map(async (device) => {
          try {
            const accessCode = await this.seam.generateAccessCode(device.id, {
              guest_reservation_id: reservationId,
            });
            return accessCode?.code
              ? {
                  deviceId: device.id,
                  deviceName: device.display_name || device.device_name || 'Smart Lock',
                  code: accessCode.code,
                  validity: this.validity(accessCode),
                }
              : null;
          } catch (_err) {
            return null;
          }
        })
      );
      return codes.filter((item): item is ReservationLockCode => item !== null);
    });
  }

  private cached<T>(cache: Map<string, CacheEntry<T>>, key: string, load: () => Promise<T>): Promise<T> {
    const entry = cache.get(key);
    if (entry && Date.now() - entry.fetchedAt < CACHE_TTL_MS) {
      return entry.request;
    }
    const request = load();
    cache.set(key, { fetchedAt: Date.now(), request });
    // A failed lookup must not be served from cache on the next hover.
    request.catch(() => {
      if (cache.get(key)?.request === request) {
        cache.delete(key);
      }
    });
    return request;
  }

  private validity(accessCode: SeamAccessCode): string | null {
    const start = DateTime.fromISO(accessCode.starts_at);
    const end = DateTime.fromISO(accessCode.ends_at);
    if (!start.isValid || !end.isValid) {
      return null;
    }
    return `${new TranslationPipe().transform('Active')} ${this.edge(start)} – ${this.edge(end)}`;
  }

  private edge(day: DateTime): string {
    return `${day.toFormat('MMM d')}, ${day.toFormat('h:mma').toLowerCase()}`;
  }
}
